import { useEffect, useRef, useState, useCallback } from "react";
import { VehiclesAPI } from "@/lib/api";
import logger from "@/lib/logger";

/**
 * Shared telemetry auto-fill hook for AddTripDialog and AddEnergyDialog.
 *
 * Encapsulates the v2.3 → v2.5 pattern in one place:
 *   • CREATE-mode gating — never overwrites in edit mode (via `lockForEdit()`)
 *   • Manual edits are sacrosanct — user-edit refs are the SINGLE source of
 *     truth (we deliberately do NOT also gate on `!value` because synchronous
 *     setState clears don't reflect in the closure until next render — that
 *     was the v2.4 stale-closure bug).
 *   • Cross-vehicle stale-clear — switching the vehicle dropdown clears
 *     previously-auto-filled values for the OLD vehicle before fetching the
 *     new one's telemetry, so a vehicle with no data lands cleanly empty.
 *   • Fetch failures degrade silently via `logger.warn` — the dialog must
 *     stay usable even if /api/vehicles/{id}/last-telemetry is unreachable.
 *
 * Usage:
 *   const autoFill = useTelemetryAutoFill({
 *     open, isEdit, vehicleId,
 *     enabled: isElectric,    // optional hard gate (e.g. type==='charge')
 *     scope: 'trip',          // logger tag
 *     fields: {
 *       odo:  { sourceField: 'last_odometer_km',  setValue: setStartOdo },
 *       batt: { sourceField: 'current_battery_pct', setValue: setStartBatt,
 *               enabled: isElectric },
 *     },
 *   });
 *
 *   // Render — show AUTO badge when autoFill.flags.odo is true.
 *   // Input onChange — call autoFill.markUserEdited('odo') before setState.
 *   // Edit-mode init — call autoFill.lockForEdit() so the autofill never runs.
 */
export default function useTelemetryAutoFill({
  open,
  isEdit,
  vehicleId,
  enabled = true,
  scope = "telemetry",
  fields,
}) {
  // Stable signature so callbacks don't re-create on every render even though
  // `fields` is a fresh object each time.
  const fieldKeys = Object.keys(fields || {});
  const sig = fieldKeys.join("|");

  const userEditedRefs = useRef({});
  const autoFilledVehicleIdRef = useRef(null);
  const [flags, setFlags] = useState({});

  // Lazily initialise refs for any newly-seen field key.
  fieldKeys.forEach((k) => {
    if (!(k in userEditedRefs.current)) userEditedRefs.current[k] = false;
  });

  /** Mark a field as manually edited — call from the input onChange BEFORE setValue. */
  const markUserEdited = useCallback((key) => {
    userEditedRefs.current[key] = true;
    setFlags((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  }, []);

  /** Hard reset (used internally on every dialog open). */
  const reset = useCallback(() => {
    fieldKeys.forEach((k) => {
      userEditedRefs.current[k] = false;
    });
    autoFilledVehicleIdRef.current = null;
    setFlags({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  /** Lock all fields against autofill — called by dialogs during edit-mode init. */
  const lockForEdit = useCallback(() => {
    fieldKeys.forEach((k) => {
      userEditedRefs.current[k] = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Reset every time the dialog (re-)opens. Hook is registered before the
  // dialog's own init effect, so refs are cleared first, then edit-mode init
  // calls `lockForEdit()` to re-lock if needed.
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  // Auto-fill effect.
  useEffect(() => {
    if (!open || isEdit || !enabled || !vehicleId) return;

    const switchedVehicle =
      autoFilledVehicleIdRef.current &&
      autoFilledVehicleIdRef.current !== vehicleId;

    if (switchedVehicle) {
      // Clear stale auto-filled values from the previous vehicle. Manual
      // edits (userEditedRef === true) are preserved. We do NOT also gate on
      // the field's CURRENT value because that would be a stale closure read.
      fieldKeys.forEach((k) => {
        const f = fields[k];
        if (f && f.enabled !== false && !userEditedRefs.current[k]) {
          f.setValue("");
        }
      });
      setFlags({});
    }

    let cancelled = false;
    (async () => {
      try {
        const t = await VehiclesAPI.lastTelemetry(vehicleId);
        if (cancelled) return;
        const nextFlags = {};
        let anyFilled = false;
        fieldKeys.forEach((k) => {
          const f = fields[k];
          if (!f || f.enabled === false) return;
          const v = t?.[f.sourceField];
          if (v != null && !userEditedRefs.current[k]) {
            f.setValue(String(v));
            nextFlags[k] = true;
            anyFilled = true;
          }
        });
        if (anyFilled) {
          setFlags(nextFlags);
          autoFilledVehicleIdRef.current = vehicleId;
        } else if (switchedVehicle) {
          // New vehicle returned no telemetry — record the swap so the next
          // vehicle change knows there's nothing to clear.
          autoFilledVehicleIdRef.current = vehicleId;
        }
      } catch (err) {
        if (!cancelled) {
          logger.warn(`${scope}-auto-fill-skipped`, err?.message || String(err), { vehicleId });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `fields` and `setValue` callbacks are not in deps on purpose — they
    // change every render but the field SHAPE is stable per dialog. Re-fetch
    // is keyed only on inputs that actually change which vehicle/state we're
    // pulling telemetry for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, vehicleId, enabled, sig]);

  return { flags, markUserEdited, lockForEdit, reset };
}
